import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getPublicPortalByDeploymentId,
  getPublicPortalCases,
  getPublicPortalCaseTemplates,
  getPublicPortalDetails,
  getPublicPortalDevices,
  getPublicPortalLogo,
  getPublicPortals,
  getPublicPortalTarget,
} from "@/api/resources/publicPortals/api";
import i18n from "@/providers/i18n";

import type { PortalAccessBody, PortalDevicesBodyInput } from "@waku-care/types/contracts/internal";

const entity = i18n.t("public-portal");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("public-portals");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const publicPortalsQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "publicPortals"],
  lists: () => [...publicPortalsQueryKeys.all(), "list"],
  listAll: () => [...publicPortalsQueryKeys.lists(), "all"],
  byDeploymentId: (deployment_id?: string) => [...publicPortalsQueryKeys.all(), "byDeploymentId", deployment_id],
  publicPortalDetails: (args: PortalAccessBody) => [...publicPortalsQueryKeys.all(), "publicPortalDetails", args],
  publicPortalLogo: (args: PortalAccessBody) => [...publicPortalsQueryKeys.all(), "publicPortalLogo", args],
  publicPortalTarget: (args: PortalAccessBody) => [...publicPortalsQueryKeys.all(), "publicPortalTarget", args],
  publicPortalDevices: (args: PortalDevicesBodyInput) => [...publicPortalsQueryKeys.all(), "publicPortalDevices", args],
  publicPortalCaseTemplates: (args: PortalAccessBody) => [
    ...publicPortalsQueryKeys.all(),
    "publicPortalCaseTemplates",
    args,
  ],
  publicPortalCases: (args: PortalAccessBody) => [...publicPortalsQueryKeys.all(), "publicPortalCases", args],
};
export const publicPortalsQueries = {
  listAll: () =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.listAll()],
      queryFn: () => getPublicPortals(),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byDeploymentId: (deployment_id?: string) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.byDeploymentId(deployment_id)],
      queryFn: deployment_id ? () => getPublicPortalByDeploymentId(deployment_id) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  publicPortalDetails: (args: PortalAccessBody) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.publicPortalDetails(args)],
      queryFn: () => getPublicPortalDetails(args),

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  publicPortalLogo: (args: PortalAccessBody) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.publicPortalLogo(args)],
      queryFn: () => getPublicPortalLogo(args),
      staleTime: 1000 * 60 * 60, // the signed url is valid for a day, no need to re-mint per mount

      // deliberately no errorMessage: a missing logo falls back to the app logo, that is
      // not worth a notification on a public page.
    }),
  publicPortalTarget: (args: PortalAccessBody) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.publicPortalTarget(args)],
      queryFn: () => getPublicPortalTarget(args),

      // deliberately no errorMessage: an invalid pin resolves to `null`, not a
      // query error (both when probing whether the portal has a pin and on a wrong pin),
      // so the pin field can show its own inline error without any global notification.
    }),
  publicPortalDevices: (args: PortalDevicesBodyInput) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.publicPortalDevices(args)],
      queryFn: () => getPublicPortalDevices(args),

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  publicPortalCaseTemplates: (args: PortalAccessBody) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.publicPortalCaseTemplates(args)],
      queryFn: () => getPublicPortalCaseTemplates(args),

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  publicPortalCases: (args: PortalAccessBody) =>
    queryOptions({
      queryKey: [...publicPortalsQueryKeys.publicPortalCases(args)],
      queryFn: () => getPublicPortalCases(args),

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
